//
// Copyright (c) 2011-2025 by Curalate, Inc.
//

import { useMutation, useQueryClient } from '@tanstack/react-query';

import { RIGHTS_STATUS } from '@apis/rights-management/rightsManagementApiModels';
import Cuid from '@models/Cuid';
import { QueryKeyConstants } from '@queries/QueryKeyConstants';
import { getNgRedux } from '@react-bridge/redux-bridge';
import { setRightsStatusChanged } from '@uia/rights-request/rights-request-actions';
import { IRightsRequest } from '@uia/rights-request/rights-request-model';

import {
  requestMultiHandleRightsGraphApi,
  requestRights,
} from 'app/bulk-actions/universal-image-actions-api';

export const useRequestRightsMutation = (cuid: Cuid) => {
  const queryClient = useQueryClient();

  const mutationFn = async (
    params: IRightsRequest & { shouldUseGraphApi: boolean },
  ) => {
    if (params.shouldUseGraphApi) {
      const partialCuid = Cuid.toPartial(cuid);

      await requestMultiHandleRightsGraphApi({ partialCuid, params });
    } else {
      await requestRights(cuid, params);
    }
  };

  return useMutation({
    mutationFn,
    onSettled: async () => {
      await Promise.all([
        queryClient.invalidateQueries({
          queryKey: [
            QueryKeyConstants.UniversalImageActions,
            QueryKeyConstants.UniversalImageActionsRights,
            QueryKeyConstants.UniversalImageActionsMetadata,
            cuid,
          ],
        }),
        queryClient.invalidateQueries({
          queryKey: [
            QueryKeyConstants.UniversalImageActions,
            QueryKeyConstants.UniversalImageActionsRights,
            QueryKeyConstants.UniversalImageActionsRightsConfigurations,
            cuid,
          ],
        }),
        queryClient.invalidateQueries({
          queryKey: [
            QueryKeyConstants.MediaFilters,
            QueryKeyConstants.MediaFiltersMediaItems,
          ],
        }),
      ]);
      getNgRedux().dispatch(setRightsStatusChanged(RIGHTS_STATUS.INITIATED));
    },
  });
};
